import type { ClientProfile, ProfileTestResponse, LocalStatusResponse } from '@veridia/shared'

type ProxyHeaders = { Authorization?: string; 'Content-Type'?: string }

function proxyHeaders(proxyToken: string | null, json = false): ProxyHeaders {
  const h: ProxyHeaders = {}
  if (proxyToken) h.Authorization = `Bearer ${proxyToken}`
  if (json) h['Content-Type'] = 'application/json'
  return h
}

export async function listProfiles(
  proxyUrl: string,
  proxyToken: string | null,
): Promise<ClientProfile[]> {
  const r = await fetch(`${proxyUrl}/local/profiles`, { headers: proxyHeaders(proxyToken) })
  const data = await r.json() as { profiles: ClientProfile[] }
  return data.profiles
}

export async function createProfile(
  proxyUrl: string,
  proxyToken: string | null,
  name: string,
  databaseUrl: string,
): Promise<ClientProfile> {
  const r = await fetch(`${proxyUrl}/local/profiles`, {
    method: 'POST',
    headers: proxyHeaders(proxyToken, true),
    body: JSON.stringify({ name, databaseUrl }),
  })
  if (!r.ok) {
    const err = await r.json() as { error?: string }
    throw new Error(err.error ?? 'Error al crear perfil')
  }
  return r.json() as Promise<ClientProfile>
}

export async function updateProfile(
  proxyUrl: string,
  proxyToken: string | null,
  id: string,
  updates: { name?: string; databaseUrl?: string },
): Promise<ClientProfile> {
  const r = await fetch(`${proxyUrl}/local/profiles/${id}`, {
    method: 'PUT',
    headers: proxyHeaders(proxyToken, true),
    body: JSON.stringify(updates),
  })
  if (!r.ok) {
    const err = await r.json() as { error?: string }
    throw new Error(err.error ?? 'Error al actualizar perfil')
  }
  return r.json() as Promise<ClientProfile>
}

export async function deleteProfile(
  proxyUrl: string,
  proxyToken: string | null,
  id: string,
): Promise<void> {
  const r = await fetch(`${proxyUrl}/local/profiles/${id}`, {
    method: 'DELETE',
    headers: proxyHeaders(proxyToken),
  })
  if (!r.ok) {
    const err = await r.json() as { error?: string }
    throw new Error(err.error ?? 'Error al eliminar perfil')
  }
}

export async function activateProfile(
  proxyUrl: string,
  proxyToken: string | null,
  id: string,
): Promise<{ success: boolean; requiresRestart: boolean }> {
  const r = await fetch(`${proxyUrl}/local/profiles/${id}/activate`, {
    method: 'POST',
    headers: proxyHeaders(proxyToken),
  })
  if (!r.ok) {
    const err = await r.json() as { error?: string }
    throw new Error(err.error ?? 'Error al activar perfil')
  }
  return r.json()
}

export async function testProfile(
  proxyUrl: string,
  proxyToken: string | null,
  id: string,
): Promise<ProfileTestResponse> {
  const r = await fetch(`${proxyUrl}/local/profiles/${id}/test`, {
    method: 'POST',
    headers: proxyHeaders(proxyToken),
  })
  return r.json() as Promise<ProfileTestResponse>
}

export async function getLocalStatus(
  proxyUrl: string,
  proxyToken: string | null,
): Promise<LocalStatusResponse> {
  try {
    const r = await fetch(`${proxyUrl}/local/status`, {
      headers: proxyHeaders(proxyToken),
      signal: AbortSignal.timeout(3000),
    })
    return r.json() as Promise<LocalStatusResponse>
  } catch {
    return { connected: false, error: 'Sin respuesta del proxy local' }
  }
}

export async function relaunch(
  proxyUrl: string,
  proxyToken: string | null,
): Promise<void> {
  await fetch(`${proxyUrl}/local/relaunch`, {
    method: 'POST',
    headers: proxyHeaders(proxyToken),
  })
}

// ── Local-first database switch (applies after relaunch) ────────────────────

export interface LocalFirstState {
  enabled: boolean
  topology: 'local' | 'remote'
}

export async function getLocalFirst(proxyUrl: string, proxyToken: string | null): Promise<LocalFirstState> {
  const r = await fetch(`${proxyUrl}/local/local-first`, { headers: proxyHeaders(proxyToken) })
  if (!r.ok) throw new Error('No se pudo leer la configuración de la base local')
  return r.json() as Promise<LocalFirstState>
}

// Rejects with the proxy's own message (e.g. unreplicated changes pending).
export async function setLocalFirst(proxyUrl: string, proxyToken: string | null, enabled: boolean): Promise<void> {
  const r = await fetch(`${proxyUrl}/local/local-first`, {
    method: 'POST',
    headers: proxyHeaders(proxyToken, true),
    body: JSON.stringify({ enabled }),
  })
  if (!r.ok) {
    const body = await r.json().catch(() => ({})) as { error?: string }
    throw new Error(body.error ?? 'No se pudo guardar la configuración de la base local')
  }
}
